import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

export default function StatCard({ title, value, interval, trend, icon: Icon, color }) {

  const trendValues = { up: '↑', down: '↓', neutral: '=' };

  return (
    <Card 
      variant="outlined" 
        sx={{ 
        height: '100%', 
        flexGrow: 1,
        minHeight: '200px',
        borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: (theme) => 
              theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
            pointerEvents: 'none',
          }
        }}
      >
        <CardContent 
          sx={{ 
            position: 'relative', 
            zIndex: 1,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 4
          }}
        >
          {/* 顶部：玻璃效果标签（类似 FEATURED） */}
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.75,
              mb: 4,
              backgroundColor: 'transparent',
              borderRadius: '8px',
              border: (theme) => 
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.15)'
                  : '1px solid rgba(255, 255, 255, 0.4)',
              width: 'fit-content',
              boxShadow: (theme) => 
                theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                  : '0 2px 8px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: (theme) => 
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
                    : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                borderRadius: '8px 8px 0 0',
              }
            }}
          >
            <Typography 
              component="h2" 
              variant="caption" 
              sx={{ 
                color: 'text.secondary', 
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontSize: '0.875rem',
                fontWeight: 700,
                opacity: 0.9,
                position: 'relative',
                zIndex: 1
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* 中间偏上：大标题（类似 Electric Card） */}
          <Stack
            direction="row"
            sx={{ 
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: 3,
              gap: 2
            }}
          >
            <Typography 
              variant="h2" 
              component="p" 
              fontWeight="bold" 
              sx={{ 
                fontSize: { xs: '2.75rem', sm: '3.25rem', md: '3.75rem' },
                lineHeight: 1,
                color: 'text.primary',
                flex: 1
              }}
            >
              {value}
            </Typography>
            {Icon && (
              <Icon 
                sx={{ 
                  fontSize: { xs: 40, sm: 48, md: 56 }, 
                  color: color || 'primary.main', 
                  opacity: 0.5,
                  flexShrink: 0,
                  mt: 0.5
                }} 
              />
            )}
          </Stack>

          {/* 中间：描述文字区域（类似 "An electric border..."） */}
          {interval && (
            <Box
              sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.75,
                mb: 'auto',
                mt: 1,
                backgroundColor: 'transparent',
                borderRadius: '8px',
                border: (theme) => 
                  theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.15)'
                    : '1px solid rgba(255, 255, 255, 0.4)',
                width: 'fit-content',
                maxWidth: '100%',
                boxShadow: (theme) => 
                  theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                    : '0 2px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: (theme) => 
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
                      : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                  borderRadius: '8px 8px 0 0',
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  opacity: 0.9,
                  position: 'relative',
                  zIndex: 1
                }}
              >
                {interval}
              </Typography>
            </Box>
          )}

          {/* 底部：玻璃效果状态标签（类似 Live v1.0） */}
          <Box sx={{ mt: 'auto', pt: 3 }}>
            {trend && (
              <Box
                sx={{
                  display: 'inline-block',
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  border: (theme) => 
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.15)'
                      : '1px solid rgba(255, 255, 255, 0.4)',
                  width: 'fit-content',
                  boxShadow: (theme) => 
                    theme.palette.mode === 'dark'
                      ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: (theme) => 
                      theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)'
                        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                    borderRadius: '8px 8px 0 0',
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    opacity: 0.9,
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {trendValues[trend]}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
    </Card>
  );
}

